import {
    setMaxPages,
    setTotalCount,
    toggleCheckedItem,
    setSelectedUser,
    setTabCounts,
    setEditedUser,
    toggleEditModal,
    openMessageModal
} from '../../store/usersSlice';
import ApiService from '../../components/api_services/apiService.jsx';
import StatusBadge from '../../components/components/status_badge/StatusBadge.jsx';
import ActionMenu from '../../components/components/action_menu/ActionMenu.jsx';
import { ReactComponent as StatusUp } from '../../components/icons/status-up.svg';
import { ReactComponent as PersonalCard } from '../../components/icons/personalcard.svg';
import { ReactComponent as Chart } from '../../components/icons/chart.svg';
import { ReactComponent as Edit } from '../../components/icons/edit.svg';
import { ReactComponent as Lock } from '../../components/icons/lock.svg';
import { ReactComponent as Danger } from '../../components/icons/danger.svg';
import {
    ADMIN_PROFILES_PAGE_SIZE,
    buildProfilesQuery,
    extractProfilesEntries,
    extractProfilesNumberOfPages,
    filterUsersForTab,
    normalizeProfilesToUsers,
    sortRowsLocally,
} from './profilesAdapter.js';

const ITEMS_PER_PAGE = 10;
const MAX_BACKEND_PROFILE_PAGES_SCAN = 200;

const SEX_LABELS = {
    MALE: 'mężczyzna',
    FEMALE: 'kobieta',
    OTHER: 'inna',
    UNSPECIFIED: '-',
};

const normalizeText = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text.length > 0 ? text : fallback;
};

const mapSex = (sex, optionalSexName) => {
    if (sex === 'OTHER' && optionalSexName) return optionalSexName;
    return SEX_LABELS[sex] || '-';
};

const buildFullName = (profile = {}) => {
    const first = normalizeText(profile?.name, '').trim();
    const last = normalizeText(profile?.surname, '').trim();
    const fullName = [first, last].filter(Boolean).join(' ').trim();
    return fullName || '-';
};

const formatReportDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const mapProfileToReportUser = (profile, fallbackId) => {
    if (!profile) {
        return {
            profileUuid: fallbackId || null,
            nickname: '-',
            fullName: '-',
            gender: '-',
            email: '-',
            description: '',
            birthday: '',
            sex: '',
            optionalSexName: '',
        };
    }

    return {
        profileUuid: profile.uuid || fallbackId || null,
        nickname: normalizeText(profile.nick, '-'),
        fullName: buildFullName(profile),
        gender: mapSex(profile.sex, profile.optionalSexName),
        email: normalizeText(profile.email, '-'),
        description: normalizeText(profile.description, ''),
        birthday: normalizeText(profile.birthday, ''),
        sex: normalizeText(profile.sex, ''),
        optionalSexName: normalizeText(profile.optionalSexName, ''),
    };
};

const fetchProfilesMap = async (ids, requestOptions = {}) => {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    const results = await Promise.allSettled(
        uniqueIds.map(async (id) => {
            const profile = await ApiService.getProfileById(id, requestOptions);
            return { id, profile };
        })
    );

    const map = new Map();
    results.forEach((result) => {
        if (result.status === 'fulfilled') {
            map.set(result.value.id, result.value.profile);
        }
    });

    return map;
};

const fetchReportRows = async (requestOptions = {}) => {
    const reportsResponse = await ApiService.getUserReports(requestOptions);
    const reports = Array.isArray(reportsResponse)
        ? reportsResponse
        : Array.isArray(reportsResponse?.data)
            ? reportsResponse.data
            : [];

    const profileIds = reports.flatMap((report) => [report?.reportedUserId, report?.reportSenderId]);
    const profilesMap = await fetchProfilesMap(profileIds, requestOptions);

    return reports.map((report, index) => {
        const reportedUserId = report?.reportedUserId || null;
        const reportSenderId = report?.reportSenderId || null;
        const reportedProfile = profilesMap.get(reportedUserId);
        const senderProfile = profilesMap.get(reportSenderId);

        const reported = mapProfileToReportUser(reportedProfile, reportedUserId);
        const sender = mapProfileToReportUser(senderProfile, reportSenderId);
        const reporterName = sender.nickname !== '-'
            ? `${sender.nickname} (${sender.fullName})`
            : normalizeText(reportSenderId, '-');

        return {
            id: report?.reportId ?? `rep-${index + 1}`,
            reportId: report?.reportId ?? null,
            reportedUserId,
            reportSenderId,
            createdAt: normalizeText(report?.createdAt, ''),
            profileUuid: reported.profileUuid,
            nickname: reported.nickname,
            fullName: reported.fullName,
            gender: report?.sex ? mapSex(report?.sex, '') : reported.gender,
            email: normalizeText(report?.email, reported.email),
            phone: normalizeText(report?.phoneNumber, '-'),
            reportDate: formatReportDateTime(report?.createdAt),
            reporterName,
            reportReason: normalizeText(report?.reason, '-'),
            status: 'pending',
            createdPacks: '-',
            likedEvents: '-',
            description: reported.description,
            birthday: reported.birthday,
            sex: normalizeText(report?.sex, reported.sex),
            optionalSexName: reported.optionalSexName,
            role: 'reports',
        };
    });
};

const filterReports = (reports, keyword, filters = {}) => {
    const lowerKeyword = normalizeText(keyword, '').toLowerCase();
    let filtered = [...reports];

    if (lowerKeyword) {
        filtered = filtered.filter((report) => (
            String(report.nickname || '').toLowerCase().includes(lowerKeyword) ||
            String(report.fullName || '').toLowerCase().includes(lowerKeyword) ||
            String(report.email || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reportReason || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reporterName || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reportedUserId || '').toLowerCase().includes(lowerKeyword) ||
            String(report.reportSenderId || '').toLowerCase().includes(lowerKeyword)
        ));
    }

    if (filters.gender) {
        filtered = filtered.filter((report) => report.gender === filters.gender);
    }
    if (filters.status) {
        filtered = filtered.filter((report) => report.status === filters.status);
    }

    return filtered;
};

const getModerationTargetId = (user) => user?.profileUuid || user?.reportedUserId || null;

const promptForReason = (defaultReason = '') => {
    const normalizedDefaultReason = defaultReason && defaultReason !== '-' ? defaultReason : '';
    const enteredReason = window.prompt('Podaj powód:', normalizedDefaultReason);
    if (enteredReason === null) return null;
    return enteredReason.trim();
};

const runReportModerationAction = async (user, actionLabel, requestFn) => {
    const targetId = getModerationTargetId(user);
    if (!targetId) {
        alert('Brak ID użytkownika do wykonania akcji.');
        return;
    }

    const reason = promptForReason(user?.reportReason);
    if (reason === null) {
        return;
    }

    if (!reason) {
        alert('Powód jest wymagany.');
        return;
    }

    try {
        await requestFn({
            id: targetId,
            reason,
        });
        alert(`${actionLabel} zostało wysłane.`);
    } catch (error) {
        console.error(`Nie udało się wykonać akcji "${actionLabel}":`, error);
    }
};

export const fetchUsers = async (
    currentPage,
    sortBy,
    sortDirection,
    keyword,
    pageTab,
    filters,
    dispatch,
    setRawUsers,
    requestOptions = {}
) => {
    if (pageTab === 'reports') {
        try {
            if (requestOptions?.signal?.aborted) {
                return;
            }

            const reportRows = await fetchReportRows(requestOptions);
            const filteredReports = filterReports(reportRows, keyword, filters || {});
            const sortedReports = sortRowsLocally(filteredReports, sortBy, sortDirection);

            dispatch(setTabCounts({
                usersCount: 0,
                adminsCount: 0,
                reportsCount: reportRows.length,
                blockedCount: 0,
            }));

            dispatch(setTotalCount(sortedReports.length));
            const totalPages = Math.max(1, Math.ceil(sortedReports.length / ITEMS_PER_PAGE));
            dispatch(setMaxPages(totalPages));

            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
            const paginated = sortedReports.slice(startIndex, startIndex + ITEMS_PER_PAGE);
            setRawUsers(paginated);
            return;
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                return;
            }
            console.error('Error fetching reports from /api/user/report:', error);
            setRawUsers([]);
            dispatch(setTotalCount(0));
            dispatch(setMaxPages(Math.max(1, currentPage)));
            dispatch(setTabCounts({ usersCount: 0, adminsCount: 0, reportsCount: 0, blockedCount: 0 }));
            return;
        }
    }

    try {
        const targetStartIndex = (currentPage - 1) * ADMIN_PROFILES_PAGE_SIZE;
        const targetEndIndex = targetStartIndex + ADMIN_PROFILES_PAGE_SIZE;

        let backendPage = 1;
        let exhausted = false;
        let scannedPages = 0;
        let collectedFilteredUsers = [];

        while (!exhausted && scannedPages < MAX_BACKEND_PROFILE_PAGES_SCAN) {
            const query = buildProfilesQuery({
                pageNumber: backendPage,
                pageSize: ADMIN_PROFILES_PAGE_SIZE,
                searchText: keyword || '',
                sortBy,
                sortDirection,
                tableType: 'users',
                pageTab,
            });

            if (requestOptions?.signal?.aborted) {
                return;
            }

            const response = await ApiService.getSystemAdminProfiles(query, requestOptions);
            const backendEntries = extractProfilesEntries(response);
            const backendNumberOfPages = extractProfilesNumberOfPages(response);

            const normalizedUsers = normalizeProfilesToUsers(backendEntries);
            const filteredUsers = filterUsersForTab(normalizedUsers, pageTab, filters || {});
            collectedFilteredUsers = [...collectedFilteredUsers, ...filteredUsers];

            exhausted = backendNumberOfPages
                ? backendPage >= backendNumberOfPages
                : backendEntries.length < ADMIN_PROFILES_PAGE_SIZE;
            backendPage += 1;
            scannedPages += 1;
        }

        const currentPageRows = collectedFilteredUsers.slice(targetStartIndex, targetEndIndex);
        const sortedCurrentPageRows = sortRowsLocally(currentPageRows, sortBy, sortDirection);

        dispatch(setTabCounts({
            usersCount: 0,
            adminsCount: 0,
            reportsCount: 0,
            blockedCount: 0,
        }));

        const hasScannedAllPages = exhausted;
        const exactTotalPages = Math.max(1, Math.ceil(collectedFilteredUsers.length / ADMIN_PROFILES_PAGE_SIZE));
        const resolvedTotalPages = hasScannedAllPages ? exactTotalPages : Math.max(exactTotalPages, exactTotalPages + 1);

        dispatch(setTotalCount(collectedFilteredUsers.length));
        dispatch(setMaxPages(resolvedTotalPages));
        setRawUsers(sortedCurrentPageRows);
    } catch (error) {
        if (error?.code === 'ERR_CANCELED') {
            return;
        }
        console.error('Error fetching users from /api/system_admin/profiles:', error);
        setRawUsers([]);
        dispatch(setTotalCount(0));
        dispatch(setMaxPages(Math.max(1, currentPage)));
        dispatch(setTabCounts({ usersCount: 0, adminsCount: 0, reportsCount: 0, blockedCount: 0 }));
    }
};

export const mapUsersToRows = (users, checkedItems, dispatch, pageTab, options = {}) => {
    const { onReportDetails } = options;

    return users.map((user) => {
        const isChecked = checkedItems.includes(user.id);
        const statusLabel = user.status === 'blocked'
            ? 'Zablokowany'
            : user.status === 'pending'
                ? 'Nieaktywowany'
                : user.status === 'forDeleting'
                    ? 'Do usunięcia'
                    : 'Aktywny';
        const statusType = user.status === 'blocked'
            ? 'inactive'
            : user.status === 'pending' || user.status === 'forDeleting'
                ? 'pending'
                : 'active';

        const defaultActionItems = [
            {
                key: 'details',
                label: 'Szczegoly',
                icon: StatusUp,
                onClick: () => dispatch(setSelectedUser(user)),
            },
            {
                key: 'message',
                label: 'Wyslij wiadomosc',
                icon: PersonalCard,
                onClick: () => dispatch(openMessageModal(user)),
            },
            {
                key: 'statistics',
                label: 'Statystyki',
                icon: Chart,
                onClick: () => dispatch(setSelectedUser(user)),
            },
            {
                key: 'edit',
                label: 'Edytuj',
                icon: Edit,
                onClick: () => {
                    dispatch(setEditedUser(user));
                    dispatch(toggleEditModal());
                },
            },
            {
                key: 'block',
                label: 'Zablokuj',
                icon: Lock,
                danger: true,
                disabled: true,
            },
        ];

        if (pageTab === 'reports') {
            const reportActionItems = [
                {
                    key: 'details',
                    label: 'Szczegoly',
                    icon: StatusUp,
                    onClick: () => {
                        if (typeof onReportDetails === 'function') {
                            onReportDetails(user);
                            return;
                        }
                        dispatch(setSelectedUser(user));
                    },
                },
                {
                    key: 'warning',
                    label: 'Ostrzezenie',
                    icon: Danger,
                    onClick: () => {
                        runReportModerationAction(user, 'Ostrzeżenie', ApiService.putWarning);
                    },
                },
                {
                    key: 'ban',
                    label: 'Blokada',
                    icon: Lock,
                    danger: true,
                    onClick: () => {
                        runReportModerationAction(user, 'Blokada', ApiService.putBan);
                    },
                },
            ];

            return [
                <label key={`checkbox-${user.id}`} className="checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => dispatch(toggleCheckedItem(user.id))}
                    />
                </label>,
                user.id,
                <span className="user-name-cell" key={`name-${user.id}`}>
                    <strong>{user.nickname}</strong> <span className="user-fullname">({user.fullName})</span>
                </span>,
                user.gender,
                <span className="email-cell">{user.email}</span>,
                user.phone,
                user.reportDate,
                user.reporterName,
                user.reportReason,
                <ActionMenu key={`actions-${user.id}`} items={reportActionItems} />,
            ];
        }

        return [
            <label key={`checkbox-${user.id}`} className="checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => dispatch(toggleCheckedItem(user.id))}
                />
            </label>,
            user.id,
            <span className="user-name-cell" key={`name-${user.id}`}>
                <strong>{user.nickname}</strong> <span className="user-fullname">({user.fullName})</span>
            </span>,
            user.gender,
            <span className="email-cell">{user.email}</span>,
            user.phone,
            user.registrationDate,
            <div className="status-cell">
                <StatusBadge status={statusType} label={statusLabel} />
            </div>,
            user.createdPacks,
            user.likedEvents,
            <ActionMenu key={`actions-${user.id}`} items={defaultActionItems} />,
        ];
    });
};
